import type { CalculatorContent, Faq } from '@/types/calculator'
import { vatDefaults } from '@/data/defaults'
import type { VatInputs } from './types'

const ratePercent = vatDefaults.rate * 100

export const content: CalculatorContent<VatInputs> = {
  intro: `Add VAT to a price or remove it from a VAT-inclusive amount with this VAT calculator for South Africa. The standard rate of ${ratePercent}% is pre-filled and can be overridden for other rates.`,
  howItWorks: [
    `Choose "Add VAT" to work out the VAT amount and gross (VAT-inclusive) price from a net amount, or "Remove VAT" to strip VAT from a gross amount and see the net price.`,
    `Adding VAT multiplies the amount by the rate; removing VAT divides by (1 + rate), because the inclusive price already contains the tax. Both directions round to cents.`,
    `The default rate is the standard South African VAT rate of ${ratePercent}%. Certain goods and services are zero-rated or exempt — this calculator applies a single rate you supply.`,
  ],
  formulas: [
    {
      label: 'Add VAT',
      expression: 'gross = net × (1 + rate); VAT = net × rate',
    },
    {
      label: 'Remove VAT',
      expression: 'net = gross ÷ (1 + rate); VAT = gross − net',
      notes: `Dividing reverses the inclusive amount — you cannot simply subtract ${ratePercent}% of the gross.`,
    },
  ],
  example: {
    title: `Adding ${ratePercent}% VAT to an invoice line`,
    description:
      'A small business needs the VAT-inclusive price and the VAT portion of a net invoice amount.',
    inputs: {
      amount: 1_000,
      direction: 'add',
      rate: vatDefaults.rate,
    },
  },
  definitions: [
    {
      term: 'VAT',
      description: `Value-Added Tax charged on most goods and services in South Africa at a standard rate of ${ratePercent}%.`,
    },
    {
      term: 'VAT-inclusive (gross)',
      description: 'The final price a customer pays, including VAT.',
    },
    {
      term: 'VAT-exclusive (net)',
      description: 'The price before VAT is added — the amount on which the tax is calculated.',
    },
    {
      term: 'Zero-rated',
      description:
        'Supplies taxed at 0% — no VAT is charged but the vendor can still claim input VAT. Examples include basic foodstuffs.',
    },
  ],
}

export const faqs: Faq[] = [
  {
    question: 'What is the VAT rate in South Africa?',
    answer: `The standard VAT rate is ${ratePercent}%. This calculator pre-fills it and lets you override it for other rates.`,
  },
  {
    question: 'How do I remove VAT from a price?',
    answer: `Divide the VAT-inclusive amount by 1 plus the rate (e.g. ÷ ${1 + vatDefaults.rate} for ${ratePercent}%). Subtracting ${ratePercent}% of the inclusive price gives the wrong answer.`,
  },
  {
    question: 'How do I add VAT to an amount?',
    answer: `Multiply the net amount by ${1 + vatDefaults.rate} (for ${ratePercent}% VAT). The difference between the gross and net is the VAT amount.`,
  },
  {
    question: 'Is this an output or input VAT tool?',
    answer:
      'Either — it converts a single amount in both directions. For returns, apply it per invoice line or to totals as needed.',
  },
]
